"use client";

import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useScroll, useTransform, useReducedMotion, useInView, type MotionValue } from "framer-motion";
import { Briefcase, Sparkles, Zap, ArrowLeftRight, Users, Mail, Phone, Eye, MessageCircle, Heart, type LucideIcon } from "lucide-react";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { useLang } from "@/lib/LanguageContext";
import { LoopVideo } from "@/components/pages/services-showcase/shared";

const EASE = [0.22, 1, 0.36, 1] as const;
const VIEW = { once: true, margin: "-80px" } as const;

function AmbientGlow({ className, color, size }: { className: string; color: string; size: number }) {
  return (
    <div
      aria-hidden
      className={`absolute pointer-events-none ${className}`}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle, ${color} 0%, transparent 65%)`,
        filter: "blur(90px)",
      }}
    />
  );
}

// ─── Why we're different ─────────────────────────────────────────────────────

function Word({ children, progress, range }: { children: string; progress: MotionValue<number>; range: [number, number] }) {
  const opacity = useTransform(progress, range, [0.16, 1]);
  return (
    <>
      <motion.span style={{ opacity }}>{children}</motion.span>{" "}
    </>
  );
}

/**
 * Paragraph whose words light up one by one. Progress is shared across all
 * paragraphs so a later one only starts once the previous is fully lit.
 */
function RevealParagraph({
  words,
  startIndex,
  totalWords,
  progress,
  className,
}: {
  words: string[];
  startIndex: number;
  totalWords: number;
  progress: MotionValue<number>;
  className: string;
}) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) return <p className={className}>{words.join(" ")}</p>;

  return (
    <p className={className}>
      {words.map((w, i) => {
        const n = startIndex + i;
        return (
          <Word key={i} progress={progress} range={[n / totalWords, (n + 1) / totalWords]}>
            {w}
          </Word>
        );
      })}
    </p>
  );
}

const PILLARS: { icon: LucideIcon; color: string }[] = [
  { icon: Eye, color: "#FFB76C" },
  { icon: MessageCircle, color: "#FF419D" },
  { icon: Heart, color: "#9B59F5" },
];

/** What consumers choose a business for — illustrates the first paragraph. */
function WhyPillars({ labels }: { labels: string[] }) {
  return (
    <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
      {labels.map((label, i) => {
        const { icon: Icon, color } = PILLARS[i % PILLARS.length];
        return (
          <motion.div
            key={label}
            className="relative flex flex-col items-center gap-3 overflow-hidden rounded-2xl sm:rounded-3xl border border-white/[0.07] px-2 py-5 sm:px-4 sm:py-7 text-center"
            style={{ background: `radial-gradient(120% 90% at 50% 0%, ${color}1c, rgb(18,13,30) 70%)` }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEW}
            transition={{ duration: 0.6, delay: i * 0.12, ease: EASE }}
          >
            <span
              className="flex h-11 w-11 sm:h-14 sm:w-14 items-center justify-center rounded-2xl"
              style={{ background: `${color}1f`, color, border: `1px solid ${color}40`, boxShadow: `0 0 30px -6px ${color}88` }}
            >
              <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
            {/* Phones: the labels carry line breaks so all three read as three even lines */}
            <span className="text-[13px] sm:text-[15px] font-medium leading-snug text-white/85 max-sm:whitespace-pre-line">{label}</span>
          </motion.div>
        );
      })}
    </div>
  );
}

/** "We know this from both sides" — illustrates the second paragraph. */
function WhySides({ sides }: { sides: { title: string; sub: string }[] }) {
  const icons = [Briefcase, Users];
  const colors = ["#FFB76C", "#9B59F5"];
  return (
    <motion.div
      className="rounded-[24px] p-px bg-gradient-to-r from-[#FFB76C]/60 via-[#FF419D]/60 to-[#9B59F5]/60"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEW}
      transition={{ duration: 0.7, ease: EASE }}
    >
      <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-4 rounded-[23px] px-4 py-6 sm:px-8 sm:py-8" style={{ background: "rgb(18,13,30)" }}>
        {sides.map((side, i) => {
          const Icon = icons[i];
          const color = colors[i];
          return (
            <div key={side.title} className={`flex flex-col items-center gap-2.5 text-center ${i === 1 ? "col-start-3" : ""}`}>
              <span
                className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full"
                style={{ background: `${color}1f`, color, border: `1px solid ${color}40` }}
              >
                <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </span>
              <span className="font-display font-bold text-base sm:text-xl text-white leading-tight">{side.title}</span>
              <span className="text-[13px] sm:text-sm text-white/50 leading-snug">{side.sub}</span>
            </div>
          );
        })}
        <motion.span
          aria-hidden
          className="col-start-2 row-start-1 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#FFB76C] via-[#FF419D] to-[#9B59F5] text-white shadow-[0_0_30px_-4px_rgba(255,65,157,0.6)]"
          initial={{ scale: 0, rotate: -90 }}
          whileInView={{ scale: 1, rotate: 0 }}
          viewport={VIEW}
          transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
        >
          <ArrowLeftRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </motion.span>
      </div>
    </motion.div>
  );
}

export function WhyDifferentSection() {
  const { t } = useLang();
  const a = t.about;
  const paragraphs = a.whySub.split("\n\n").map((p) => p.split(" "));
  const totalWords = paragraphs.reduce((sum, p) => sum + p.length, 0);
  const startIndices = paragraphs.map((_, i) => paragraphs.slice(0, i).reduce((sum, p) => sum + p.length, 0));
  const textRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: textRef, offset: ["start 85%", "end 50%"] });

  return (
    // overflow-clip (not hidden) keeps the sticky heading working
    <section className="section-padding relative overflow-clip" style={{ background: "rgb(13,10,24)" }}>
      <AmbientGlow className="-left-48 -top-24" color="rgba(224,64,160,0.08)" size={700} />
      <AmbientGlow className="-right-24 -bottom-24" color="rgba(155,89,245,0.09)" size={600} />

      <div className="container relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <motion.div
          className="lg:col-span-5 lg:sticky lg:top-32"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEW}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <SectionLabel>{a.whyLabel}</SectionLabel>
          <div className="relative mt-5 pl-5 sm:pl-6">
            <motion.span
              aria-hidden
              className="absolute left-0 top-1 bottom-1 w-[3px] origin-top rounded-full bg-gradient-to-b from-[#FFB76C] via-[#FF419D] to-[#9B59F5]"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={VIEW}
              transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            />
            <h2
              className="font-display font-extrabold text-white leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(26px, 2.8vw, 40px)", letterSpacing: "-0.03em" }}
            >
              {a.whyTitle}
            </h2>
          </div>

          <motion.div
            className="relative mt-8 sm:mt-10 aspect-[16/10] lg:aspect-[4/3] overflow-hidden rounded-[24px] border border-white/[0.08] shadow-[0_30px_80px_-30px_rgba(224,64,160,0.45)]"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={VIEW}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          >
            <Image
              src="/background-images/rule-your-brand.webp"
              alt=""
              fill
              sizes="(min-width: 1024px) 40vw, 100vw"
              className="object-cover object-[center_65%]"
            />
            <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[rgb(13,10,24)]/70 via-transparent to-transparent" />
          </motion.div>
        </motion.div>

        <div ref={textRef} className="lg:col-span-7 flex flex-col gap-10 sm:gap-14 lg:pt-12">
          {paragraphs.map((words, i) => (
            <div key={i} className="flex flex-col gap-10 sm:gap-14">
              {i > 0 && (
                <motion.span
                  aria-hidden
                  className="block h-px w-full origin-left bg-gradient-to-r from-white/25 via-white/10 to-transparent"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={VIEW}
                  transition={{ duration: 1, ease: EASE }}
                />
              )}
              <RevealParagraph
                words={words}
                startIndex={startIndices[i]}
                totalWords={totalWords}
                progress={scrollYProgress}
                className="font-display text-lg sm:text-xl lg:text-[22px] leading-[1.5] text-white"
              />
              {i === 0 && <WhyPillars labels={a.whyPillars} />}
              {i === 1 && <WhySides sides={a.whySides} />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── AI — a tool, not a replacement ──────────────────────────────────────────

const STAGES: { icon: LucideIcon; color: string }[] = [
  { icon: Briefcase, color: "#FFB76C" },
  { icon: Sparkles, color: "#9B59F5" },
  { icon: Zap, color: "#FF419D" },
];

function StageNode({ icon: Icon, color, index }: { icon: LucideIcon; color: string; index: number }) {
  return (
    <motion.span
      className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border"
      style={{ background: "rgb(18,13,30)", borderColor: `${color}55`, color }}
      initial={{ opacity: 0, scale: 0.6 }}
      whileInView={{ opacity: 1, scale: 1, boxShadow: `0 0 36px -4px ${color}88` }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 + index * 0.35, ease: EASE }}
    >
      <Icon size={22} />
    </motion.span>
  );
}

export function AiToolSection() {
  const { t } = useLang();
  const a = t.about;
  const bodies = [a.aiBody1, a.aiBody2, a.aiBody3];
  const trackRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start 80%", "end 60%"] });
  const fill = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section className="section-padding relative overflow-clip" style={{ background: "rgb(13,10,24)" }}>
      <AmbientGlow className="-right-48 -top-24" color="rgba(155,89,245,0.1)" size={800} />
      <AmbientGlow className="-left-24 -bottom-24" color="rgba(74,158,255,0.07)" size={600} />

      <div className="container relative z-10">
        <motion.div
          className="mx-auto max-w-4xl text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEW}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <SectionLabel>{a.aiLabel}</SectionLabel>
          <h2
            className="mt-4 font-display font-extrabold text-white leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(32px, 4.2vw, 60px)", letterSpacing: "-0.03em" }}
          >
            {a.aiTitle}
          </h2>
        </motion.div>

        {/* Progression: experience → AI → both. Line fills with scroll. */}
        <div ref={trackRef} className="relative mt-14 sm:mt-20">
          {/* Connector — horizontal on desktop, vertical on mobile */}
          <div aria-hidden className="absolute left-[27px] top-7 bottom-7 w-px bg-white/[0.08] lg:left-[calc(100%/6)] lg:right-[calc(100%/6)] lg:top-[27px] lg:bottom-auto lg:h-px lg:w-auto">
            <motion.span
              className="absolute inset-0 origin-top bg-gradient-to-b from-[#FFB76C] via-[#9B59F5] to-[#FF419D] lg:hidden"
              style={{ scaleY: fill }}
            />
            <motion.span
              className="absolute inset-0 hidden origin-left bg-gradient-to-r from-[#FFB76C] via-[#9B59F5] to-[#FF419D] lg:block"
              style={{ scaleX: fill }}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-8">
            {bodies.map((body, i) => {
              const { icon, color } = STAGES[i];
              const last = i === bodies.length - 1;
              return (
                <div key={i} className="flex gap-5 lg:flex-col lg:items-center lg:text-center lg:gap-7">
                  <StageNode icon={icon} color={color} index={i} />
                  <motion.div
                    className={
                      last
                        ? "rounded-[24px] p-px bg-gradient-to-br from-[#FFB76C] via-[#FF419D] to-[#9B59F5] shadow-[0_30px_80px_-30px_rgba(255,65,157,0.45)]"
                        : ""
                    }
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.35 + i * 0.35, ease: EASE }}
                  >
                    <p
                      className={`text-base sm:text-[17px] leading-[1.7] ${
                        last ? "rounded-[23px] p-6 sm:p-7 text-white" : "pt-3 lg:pt-0 lg:max-w-[330px] text-white/70"
                      }`}
                      style={last ? { background: "rgb(18,13,30)" } : undefined}
                    >
                      {body}
                    </p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Values (below the founders) ─────────────────────────────────────────────

const VALUES: { icon: LucideIcon; color: string }[] = [
  { icon: ArrowLeftRight, color: "#E040A0" },
  { icon: Users, color: "#9B59F5" },
  { icon: Sparkles, color: "#4A9EFF" },
];

export function ValuesList() {
  const { t } = useLang();
  const cards = t.whyAviva.cards;

  return (
    <div
      className="mt-6 overflow-hidden rounded-[24px] sm:rounded-[28px] border border-white/[0.07]"
      style={{ background: "rgb(12,9,20)" }}
    >
      {cards.map((card, i) => {
        const { icon: Icon, color } = VALUES[i % VALUES.length];
        return (
          <motion.div
            key={card.title}
            className="group relative grid grid-cols-1 md:grid-cols-12 items-start md:items-center gap-4 md:gap-10 border-t border-white/[0.06] px-6 py-7 first:border-t-0 sm:px-10 sm:py-10"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
          >
            {/* Hover wash + accent edge */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              style={{ background: `radial-gradient(70% 140% at 0% 50%, ${color}1a, transparent 70%)` }}
            />
            <motion.span
              aria-hidden
              className="absolute left-0 top-0 bottom-0 w-[2px] origin-top"
              style={{ background: `linear-gradient(180deg, ${color}, transparent)` }}
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 + i * 0.1, ease: EASE }}
            />

            <div className="relative flex items-center gap-4 md:col-span-5">
              <span
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
                style={{ background: `${color}1f`, color, border: `1px solid ${color}40` }}
              >
                <Icon size={20} />
              </span>
              <h3 className="font-display font-bold text-xl sm:text-2xl lg:text-[28px] text-white leading-snug">
                {card.title}
              </h3>
            </div>
            <p className="relative md:col-span-7 text-[15px] sm:text-base lg:text-lg text-white/55 leading-relaxed transition-colors duration-300 group-hover:text-white/75">
              {card.body}
            </p>
          </motion.div>
        );
      })}
    </div>
  );
}

// ─── Closing CTA ─────────────────────────────────────────────────────────────

const CTA_EMAIL = "office@avivadigital.bg";
const CTA_PHONE_HREF = "tel:+359886662020";
const CTA_PHONE_DISPLAY = "+359 886 66 20 20";
const FOUNDERS = [{ src: "/team/vyara-ilieva-avatar.webp?v=3" },{ src: "/team/lyubomir-iliev-avatar.webp" }];

export function AboutCta() {
  const { t } = useLang();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-10% 0px" });
  const reduceMotion = useReducedMotion();
  const active = inView && !reduceMotion;

  return (
    <section className="section-padding relative overflow-hidden">
      <AmbientGlow className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" color="rgba(224,64,160,0.14)" size={900} />

      <div className="container relative z-10">
        <motion.div
          ref={ref}
          className="relative mx-auto max-w-6xl overflow-hidden rounded-[26px] sm:rounded-[34px] p-px"
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={VIEW}
          transition={{ duration: 0.8, ease: EASE }}
        >
          {/* Rotating gradient border */}
          <motion.span
            aria-hidden
            className="absolute left-1/2 top-1/2 aspect-square w-[160%] -translate-x-1/2 -translate-y-1/2"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0%, #FF419D 12%, #FFB76C 22%, transparent 35%, transparent 55%, #9B59F5 70%, transparent 82%)",
            }}
            animate={active ? { rotate: 360 } : undefined}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          />

          <div className="relative overflow-hidden rounded-[25px] sm:rounded-[33px]" style={{ background: "rgb(12,10,20)" }}>
            {/* Video backdrop, darkened toward the copy */}
            <LoopVideo
              src="/videos/ai-master-video.mp4"
              poster="/videos/ai-master-poster.webp"
              active={active}
              className="absolute inset-0 h-full w-full object-cover opacity-60"
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 70% 80% at 50% 50%, rgba(12,10,20,0.9) 0%, rgba(12,10,20,0.75) 55%, rgba(12,10,20,0.45) 100%)",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
              }}
            />

            <div className="relative flex flex-col items-center px-6 py-14 text-center sm:px-12 sm:py-20 lg:px-20 lg:py-24">
              {/* Founders — the people you'll actually work with */}
              <motion.div
                className="inline-flex items-center gap-3 sm:gap-4 rounded-full border border-white/10 bg-white/[0.05] py-2 pl-2 pr-5 backdrop-blur-md"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEW}
                transition={{ duration: 0.6, delay: 0.15, ease: EASE }}
              >
                <span className="flex -space-x-3">
                  {FOUNDERS.map((f) => (
                    <span
                      key={f.src}
                      className="relative h-12 w-12 sm:h-14 sm:w-14 rounded-full p-[2px]"
                      style={{ background: "linear-gradient(135deg, #FFB76C, #FF419D, #9B59F5)" }}
                    >
                      <span className="relative block h-full w-full overflow-hidden rounded-full ring-2 ring-[rgb(12,10,20)]">
                        <Image src={f.src} alt="" fill sizes="56px" className="object-cover object-top" />
                      </span>
                    </span>
                  ))}
                </span>
                <span className="flex flex-col items-start text-left leading-tight">
                  <span className="text-[13px] sm:text-sm font-medium text-white">
                    {t.about.founder1Name.split(" ")[0]} & {t.about.founder2Name.split(" ")[0]}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-[11px] sm:text-xs text-white/45">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    Aviva Digital
                  </span>
                </span>
              </motion.div>

              <h2
                className="mt-8 max-w-3xl font-display font-extrabold text-white leading-[1.02] tracking-tight"
                style={{ fontSize: "clamp(36px, 5.4vw, 76px)", letterSpacing: "-0.03em" }}
              >
                {t.about.ctaTitle}
              </h2>
              <motion.span
                aria-hidden
                className="mt-7 block h-px w-48 bg-gradient-to-r from-transparent via-[#FF419D] to-transparent"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={VIEW}
                transition={{ duration: 1, delay: 0.3, ease: EASE }}
              />

              {/* Actions — the button, with email and phone as plain links beside it */}
              <div className="mt-9 flex w-full max-w-md flex-col items-center gap-5 sm:w-auto sm:max-w-none sm:flex-row sm:gap-8">
                <Link
                  href="/contact"
                  className="group relative inline-flex h-14 sm:h-16 items-center justify-center overflow-hidden rounded-2xl px-8 sm:px-10 font-display text-base sm:text-lg font-semibold text-white shadow-[0_20px_60px_-15px_rgba(255,65,157,0.6)] transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{ background: "linear-gradient(135deg, #FFB76C, #FF419D)" }}
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                  />
                  <span className="relative">{t.about.ctaButton}</span>
                </Link>
                {[
                  { href: `mailto:${CTA_EMAIL}`, icon: Mail, label: CTA_EMAIL },
                  { href: CTA_PHONE_HREF, icon: Phone, label: CTA_PHONE_DISPLAY },
                ].map(({ href, icon: Icon, label }) => (
                  <a
                    key={href}
                    href={href}
                    className="inline-flex items-center gap-2 text-[15px] font-medium text-white/80 underline-offset-4 transition-colors hover:text-white hover:underline"
                  >
                    <Icon size={17} className="text-[#FF8AC2]" />
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
